/**
 * Screen 14e — the door scanner.
 *
 * Built for a volunteer holding a phone in one hand at 6:55pm with no
 * signal, which drives every decision here:
 *
 *  * The verdict is one word in a full-width colour band. A volunteer glances
 *    at it between people; they do not read a sentence.
 *  * It answers from the cached list first and queues the scan. Waiting on a
 *    round trip per person is what makes a queue.
 *  * "Not on my list" is not the same message as "not a valid ticket". The
 *    cache can be stale; saying INVALID to a member who paid is worse than
 *    saying "check with the desk".
 *  * Manual entry by booking reference is always available. Cameras fail,
 *    screens crack, and the alternative is turning someone away.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorNote, Note, Shell } from "../components/Shell";
import { isSignedIn, OfflineError, request } from "../lib/api";
import {
  checkLocally,
  clearQueue,
  deviceId,
  loadGuestList,
  loadQueue,
  queueScan,
  saveGuestList,
  type GuestList,
  type LocalOutcome,
} from "../lib/door";
import { formatDateTime } from "../lib/format";
import type { Locale } from "../i18n";

// `BarcodeDetector` is not in the DOM lib yet.
declare const BarcodeDetector: {
  new (options?: { formats: string[] }): { detect(source: CanvasImageSource): Promise<{ rawValue: string }[]> };
} | undefined;

const BAND: Record<LocalOutcome, string> = {
  VALID: "membership",
  ALREADY: "news",
  NOT_PAID: "news",
  INVALID: "danger",
  UNKNOWN: "news",
};

const MESSAGE: Record<LocalOutcome, string> = {
  VALID: "admin.scan_valid",
  ALREADY: "admin.scan_already",
  NOT_PAID: "state.PENDING_PAYMENT",
  INVALID: "admin.scan_invalid",
  UNKNOWN: "admin.scan_unknown",
};

export default function Scan({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  const [list, setList] = useState<GuestList | null>(() => loadGuestList());
  const [programId, setProgramId] = useState<string>("");
  const [outcome, setOutcome] = useState<{ outcome: LocalOutcome; ref: string; seats: string[] } | null>(null);
  const [admitted] = useState<Set<string>>(() => new Set());
  const [queued, setQueued] = useState(loadQueue().length);
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState("");
  const [cameraOn, setCameraOn] = useState(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const pull = useCallback(async () => {
    setError(null);
    try {
      const body = await request<GuestList>(
        `/api/v1/admin/programs/${Number(programId)}/guest-list`,
      );
      saveGuestList(body);
      setList(body);
    } catch (err) {
      setError(err instanceof OfflineError ? t("error.network") : t("error.generic"));
    }
  }, [programId, t]);

  /** Apply one scan: answer locally, queue it, and push when there is signal. */
  const handle = useCallback(
    async (payload: string) => {
      const local = checkLocally(payload, list, admitted);
      setOutcome({
        outcome: local.outcome,
        ref: local.ticket?.ref ?? "",
        seats: local.ticket?.seats ?? [],
      });
      if (local.outcome !== "VALID") return;

      admitted.add(local.ticket!.ref);
      queueScan({
        qr_payload: payload,
        program_id: list!.program_id,
        device_id: deviceId(),
        scanned_at: new Date().toISOString(),
      });
      setQueued(loadQueue().length);
      if (navigator.onLine) void sync();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, admitted],
  );

  /** Drain the queue. The server decides; a conflict is reported, not hidden. */
  const sync = useCallback(async () => {
    const pending = loadQueue();
    if (pending.length === 0) return;
    try {
      const result = await request<{ applied: number; conflicts: number; results: { outcome: string; ref: string }[] }>(
        "/api/v1/admin/scan/sync",
        { method: "POST", body: { scans: pending } },
      );
      clearQueue();
      setQueued(0);
      if (result.conflicts > 0) {
        // Another door admitted this ticket first. The volunteer needs to
        // know, because they already let the person in.
        setError(
          t("admin.scan_conflicts", {
            count: result.conflicts,
            defaultValue: `${result.conflicts} already admitted at another door`,
          }),
        );
      }
    } catch {
      // Still no signal. The queue survives; nothing is lost.
    }
  }, [t]);

  useEffect(() => {
    if (online) void sync();
  }, [online, sync]);

  // Camera loop. BarcodeDetector where the browser has it; manual entry
  // otherwise, rather than shipping a decoder nobody reviewed.
  useEffect(() => {
    if (!cameraOn) return;
    let stream: MediaStream | null = null;
    let raf = 0;
    let detector: InstanceType<NonNullable<typeof BarcodeDetector>> | null = null;

    (async () => {
      try {
        if (typeof BarcodeDetector === "undefined") {
          setError(t("admin.scan_no_camera_api"));
          setCameraOn(false);
          return;
        }
        detector = new BarcodeDetector({ formats: ["qr_code"] });
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (video.current) {
          video.current.srcObject = stream;
          await video.current.play();
        }
        const tick = async () => {
          if (video.current && canvas.current && detector) {
            try {
              const found = await detector.detect(video.current);
              if (found.length > 0) await handle(found[0].rawValue);
            } catch {
              /* a frame that failed to decode is normal */
            }
          }
          raf = requestAnimationFrame(() => void tick());
        };
        void tick();
      } catch {
        setError(t("admin.scan_no_camera"));
        setCameraOn(false);
      }
    })();

    return () => {
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, [cameraOn, handle, t]);

  if (!isSignedIn()) {
    return (
      <Shell area="about" title={t("admin.scan")}>
        <Note>{t("error.session_expired")}</Note>
      </Shell>
    );
  }

  return (
    <Shell area="about" title={t("admin.scan")}>
      {/* The verdict, full width, one word. */}
      {outcome && (
        <div data-area={BAND[outcome.outcome]} className="verdict" role="status" aria-live="assertive">
          <strong className="verdict__word">{t(MESSAGE[outcome.outcome])}</strong>
          {outcome.ref && (
            <span>
              {outcome.ref} · {outcome.seats.join(", ")}
            </span>
          )}
        </div>
      )}

      <p className="fallback-note">
        {online ? "" : t("admin.scan_offline")}
        {queued > 0 && ` · ${t("admin.scan_queued", { count: queued, defaultValue: `${queued} queued` })}`}
      </p>

      {error && <ErrorNote>{error}</ErrorNote>}

      {!list ? (
        <>
          <Note>{t("admin.scan_pull_list")}</Note>
          <input
            className="field"
            inputMode="numeric"
            value={programId}
            placeholder={t("admin.programs")}
            onChange={(e) => setProgramId(e.target.value)}
          />
          <button type="button" className="btn btn--primary" disabled={!programId} onClick={() => void pull()}>
            {t("common.continue")}
          </button>
        </>
      ) : (
        <>
          <p className="fallback-note">
            {list.venue} · {formatDateTime(list.date_time, locale)} · {list.tickets.length}
          </p>

          {cameraOn ? (
            <>
              <video ref={video} className="scanner" muted playsInline />
              <canvas ref={canvas} hidden />
              <button type="button" className="btn" onClick={() => setCameraOn(false)}>
                {t("common.close")}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary" onClick={() => setCameraOn(true)}>
              {t("admin.scan")}
            </button>
          )}

          {/* Always available. A cracked screen or a dead camera must not be
              the reason someone is turned away. */}
          <h3 className="section">{t("admin.scan_manual")}</h3>
          <input
            className="field"
            value={manual}
            placeholder="ITS-2026-00001"
            onChange={(e) => setManual(e.target.value.toUpperCase())}
          />
          <button
            type="button"
            className="btn"
            disabled={!manual}
            onClick={() => {
              const found = list.tickets.find((ticket) => ticket.ref === manual.trim());
              if (found) void handle(found.qr_payload);
              else setOutcome({ outcome: "UNKNOWN", ref: manual.trim(), seats: [] });
              setManual("");
            }}
          >
            {t("admin.confirm_checkin")}
          </button>

          <button type="button" className="btn" onClick={() => void pull()}>
            {t("common.retry")}
          </button>
        </>
      )}
    </Shell>
  );
}
